import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { CheckCircle2, MessageCircle, Send } from "lucide-react";
import { WHATSAPP_LINK } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEBSITE_TYPES = ["Business Website", "E-Commerce", "Restaurant / Hotel", "Salon", "Clinic", "Real Estate", "Used Car Dealer", "Portfolio", "Custom Website", "Other"];
const BUDGETS = ["Under ₹15,000", "₹15,000 – ₹25,000", "₹25,000 – ₹40,000", "₹40,000+", "Not Sure"];

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-[border-color,box-shadow] duration-300 focus:border-accent/60 focus:ring-2 focus:ring-accent/20";

const Field = ({ label, children, error }) => (
  <div>
    <label className="mb-1.5 block font-display text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</label>
    {children}
    {error && <p className="mt-1 text-xs text-red-400" data-testid={`error-${label.toLowerCase().replace(/\s/g, "-")}`}>{error}</p>}
  </div>
);

export const EnquiryForm = () => {
  const [form, setForm] = useState({ full_name: "", business_name: "", phone: "", email: "", business_type: "", website_type: "", features: "", budget: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.full_name.trim().length < 2) errs.full_name = "Please enter your full name";
    if (!/^[0-9+\-\s]{8,15}$/.test(form.phone.trim())) errs.phone = "Enter a valid phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid email address";
    if (!form.website_type) errs.website_type = "Select a website type";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Requirement received. We'll get in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="noise relative overflow-hidden bg-black py-24 md:py-32">
      <div className="pointer-events-none absolute left-[-10%] top-0 h-[40vh] w-[50vw] rounded-full bg-royal/10 blur-[130px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 md:px-12 lg:grid-cols-[1fr_1.2fr] lg:items-start">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <p className="eyebrow">Start your project</p>
            <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.05] tracking-tighter sm:text-4xl lg:text-5xl">
              Tell us what you need. <span className="text-gradient">We'll build it.</span>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-zinc-400">
              Have an idea for your business website? Tell us about it and we'll get in touch.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enquiry-whatsapp-btn"
              className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 px-6 py-3.5 font-display text-xs font-bold uppercase tracking-[0.15em] text-[#25D366] transition-[background-color,border-color] duration-300 hover:bg-[#25D366]/20"
            >
              <MessageCircle className="h-4 w-4" />
              Prefer WhatsApp? Chat with us directly
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="glass rounded-2xl p-7 md:p-9">
            {done ? (
              <div className="flex flex-col items-center py-14 text-center" data-testid="enquiry-success">
                <CheckCircle2 className="h-14 w-14 text-accent" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-xl font-extrabold uppercase tracking-wide text-white">
                  Thank you! Your requirement has been received.
                </h3>
                <p className="mt-3 max-w-sm text-sm text-zinc-400">
                  Our team will review your details and get in touch. Prefer WhatsApp? Chat with us directly.
                </p>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="enquiry-success-whatsapp-btn"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[filter] duration-300 hover:brightness-110"
                >
                  <MessageCircle className="h-4 w-4" />
                  Chat on WhatsApp
                </a>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="enquiry-form">
                <Field label="Full Name *" error={errors.full_name}>
                  <input data-testid="input-full-name" className={inputCls} placeholder="Your name" value={form.full_name} onChange={set("full_name")} />
                </Field>
                <Field label="Business Name">
                  <input data-testid="input-business-name" className={inputCls} placeholder="Your business" value={form.business_name} onChange={set("business_name")} />
                </Field>
                <Field label="Phone Number *" error={errors.phone}>
                  <input data-testid="input-phone" className={inputCls} placeholder="98765 43210" value={form.phone} onChange={set("phone")} />
                </Field>
                <Field label="Email *" error={errors.email}>
                  <input data-testid="input-email" type="email" className={inputCls} placeholder="you@business.com" value={form.email} onChange={set("email")} />
                </Field>
                <Field label="Business Type">
                  <input data-testid="input-business-type" className={inputCls} placeholder="e.g. Salon, Clinic, Retail" value={form.business_type} onChange={set("business_type")} />
                </Field>
                <Field label="Website Type *" error={errors.website_type}>
                  <select data-testid="select-website-type" className={`${inputCls} appearance-none ${form.website_type ? "" : "text-zinc-600"}`} value={form.website_type} onChange={set("website_type")}>
                    <option value="" disabled className="bg-charcoal">Select website type</option>
                    {WEBSITE_TYPES.map((t) => <option key={t} value={t} className="bg-charcoal text-white">{t}</option>)}
                  </select>
                </Field>
                <Field label="Required Features">
                  <input data-testid="input-features" className={inputCls} placeholder="e.g. Gallery, bookings, payments" value={form.features} onChange={set("features")} />
                </Field>
                <Field label="Approximate Budget">
                  <select data-testid="select-budget" className={`${inputCls} appearance-none ${form.budget ? "" : "text-zinc-600"}`} value={form.budget} onChange={set("budget")}>
                    <option value="" disabled className="bg-charcoal">Select budget range</option>
                    {BUDGETS.map((b) => <option key={b} value={b} className="bg-charcoal text-white">{b}</option>)}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Message">
                    <textarea data-testid="input-message" rows={4} className={`${inputCls} resize-none`} placeholder="Tell us about your project..." value={form.message} onChange={set("message")} />
                  </Field>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="enquiry-submit-btn"
                  className="flex items-center justify-center gap-2 rounded-full bg-accent py-4 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,box-shadow,opacity] duration-300 hover:bg-white hover:shadow-[0_0_35px_rgba(0,229,255,0.4)] disabled:opacity-60 sm:col-span-2"
                >
                  {sending ? "Sending..." : "Send My Requirement"}
                  <Send className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
